import { ConditionEnum, PatientDetails, UpdatePatientBody } from '@vetahealth/tuna-can-api'
import { Form } from 'antd'
import { Mutex } from 'async-mutex'
import dayjs from 'dayjs'
import { produce } from 'immer'
import { debounce, invert, unset } from 'lodash-es'
import { FieldData } from 'rc-field-form/lib/interface'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { omitEqualValues } from '../../../lib/normalizers'
import { normalizePatientValues, usePatientStore } from '../../../stores/patient'
import { Address, Contact, EditFormValues, FormKeys, Personal } from '../../Forms'
import { getConditionNames } from '../../Forms/helpers'
import { SubTitle } from '../styles'

const createPartialUpdate = (patient: PatientDetails, formValues: EditFormValues): Partial<UpdatePatientBody> => {
  const conditionNameToKey = invert(getConditionNames())

  const updatedValues = {
    ...formValues,
    ...(formValues?.dateOfBirth && { [FormKeys.DATE_OF_BIRTH]: formValues.dateOfBirth.format('YYYY-MM-DD') }),
    conditions: formValues.conditions.map((conditionName) => conditionNameToKey[conditionName] ?? conditionName),
    ...(formValues.preferences && {
      preferences: produce(patient.preferences, ({ notifications }) => {
        notifications.channel = formValues.preferences.notifications.channel
      }),
    }),
  }

  return omitEqualValues(normalizePatientValues(patient), normalizePatientValues(updatedValues))
}

const removePathsWithErrors = (update: Partial<UpdatePatientBody>, pathsWithErrors: (string | number)[][]): void => {
  for (const path of pathsWithErrors) {
    unset(update, path)
  }
}

const updateMutex = new Mutex()

export function Demographics(): React.ReactNode {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [patient, updatePatient] = usePatientStore((state) => [state.patient, state.updatePatient])
  const [form] = Form.useForm()
  const conditionNames = getConditionNames()

  useEffect(() => {
    form.resetFields()
  }, [patient?.id])

  const debouncedUpdate = useCallback(
    debounce(async (patientToUpdate, update) => {
      if (!Object.values(update).length) return
      const releaseMutex = await updateMutex.acquire()

      await updatePatient(patientToUpdate, update)

      releaseMutex()
    }, 750),
    [patient],
  )

  const handleChange = (changes: FieldData[]) => {
    const { validating } = changes[0] ?? {}
    if (!patient || !form.isFieldsTouched() || validating) {
      debouncedUpdate(patient, {})
      return
    }
    const errorPaths = form
      .getFieldsError()
      .filter((fieldError) => fieldError.errors.length)
      .map((fieldError) => fieldError.name)

    const update = createPartialUpdate(patient, form.getFieldsValue())
    removePathsWithErrors(update, errorPaths)
    debouncedUpdate(patient, update)
  }

  if (!patient || patient.id !== id) return <div />

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...patient,
        conditions: patient.conditions.map(
          (conditionKey: ConditionEnum) => conditionNames[conditionKey] ?? conditionKey,
        ),
        dateOfBirth: patient.dateOfBirth !== '0000-00-00' ? dayjs(patient.dateOfBirth) : null,
        channel: patient.preferences.notifications.channel,
      }}
      validateTrigger="onChange"
      onFieldsChange={handleChange}
      scrollToFirstError
    >
      <SubTitle>{t('widgets.patientEdit.personal')}</SubTitle>
      <Personal form={form} />
      <SubTitle>{t('widgets.patientEdit.address')}</SubTitle>
      <Address />
      <SubTitle>{t('widgets.patientEdit.contact')}</SubTitle>
      <Contact />
    </Form>
  )
}
